import React, { memo } from 'react';
import dynamic from 'next/dynamic';
import * as dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import cn from 'classnames';

import { stateName } from 'utils/states';
import validateUrl from 'utils/validateUrl';

import Pluralize from 'components/Pluralize';

// Dynamic imports to reduce bundle size
const BarGraph = dynamic(() => import('components/ProgramDetails/BarGraph'));

dayjs.extend(relativeTime);

const Position = ({ className, compact, label, singular, value }) => (
  <>
    <span
      className={cn('font-semibold uppercase py-1', {
        [className]: true,
        'px-1 text-xs mr-1 block': compact,
        'px-2 text-sm block': !compact
      })}
    >
      {label}
    </span>
    <div
      className={cn('font-semibold px-1 py-2', {
        'text-xs pb-0': compact,
        'text-lg': !compact
      })}
    >
      <Pluralize singular={singular} count={parseInt(value)} />
    </div>
  </>
);

const Email = ({ address }) => {
  return typeof address === 'string' ? (
    <a
      href={`mailto:${address.toLowerCase()}`}
      className="text-blue-500 underline"
    >
      {address.toLowerCase()}
    </a>
  ) : null;
};

const ProgramDetails = ({ data, isLoading, ...props }) => {
  const { accreditation, positions } = data;
  const lengthOfTraining = parseInt(accreditation?.accreditedLengthOfTraining);
  const positionsPerYear = positions?.approved / lengthOfTraining;
  const positionType = data.type === 'Residency' ? 'resident' : 'fellow';
  const url = validateUrl(data.about.url);

  const CityState = () => (
    <>
      <span className="text-gray-900 block">{data.location.split(',')[0]}</span>
      <span className="text-gray-700">{stateName(data.state)}</span>
    </>
  );

  const ApprovedPosition = props => (
    <Position
      className="bg-teal-400 text-teal-900"
      label="Approved"
      value={positions?.approved}
      singular={positionType}
      {...props}
    />
  );

  const FilledPosition = props => (
    <Position
      className="bg-yellow-400 text-yellow-900"
      label="Filled"
      value={positions?.filled}
      singular={positionType}
      {...props}
    />
  );

  const PositionsPerYear = () => (
    <>
      <Pluralize singular={positionType} count={positionsPerYear} /> per year
    </>
  );

  // TODO: Redo this to combine with the full map / DRY.
  if (props.minimal) {
    return (
      <div className="fixed bottom-16 left-0 w-full-4 mx-2 mb-4 sm:mx-3 sm:mb-6 sm:w-full-6 bg-white shadow-xl">
        <div className="grid gap-0">
          <div className="w-full py-2 flex justify-between bg-gray-100 p-2 items-center">
            <h3 className="flex flex-col text-left p-0 text-xs">
              <CityState />
            </h3>
            <div className="flex flex-col justify-end text-gray-700 font-medium text-xs text-right">
              <span className="">{lengthOfTraining} year program</span>
              <span>
                <PositionsPerYear />
              </span>
            </div>
          </div>
          <div className="p-2">
            <div className="flex text-left gap-1">
              <div className="w-1/2">
                <ApprovedPosition compact />
              </div>
              <div className="w-1/2">
                <FilledPosition compact />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-5 pt-4 px-4">
      <div className="w-full my-1 flex justify-between">
        <h3 className="text-left p-0">
          <CityState />
        </h3>
        <div className="flex flex-col justify-end text-gray-600 font-medium text-right">
          <span className="">{lengthOfTraining} year program</span>
          <span>
            <PositionsPerYear />
          </span>
        </div>
      </div>

      {/* Positions */}
      <div className="flex text-left">
        <div className="w-1/2">
          <ApprovedPosition />
        </div>
        <div className="w-2" />
        <div className="w-1/2">
          <FilledPosition />
        </div>
      </div>

      {/* Rotations */}
      <div className="text-left">
        <div className="text-sm uppercase mb-4 text-pink-500 font-semibold">
          Rotations <span className="font-normal">(approximate)</span>
        </div>
        <div className="h-48 w-full max-w-full">
          <BarGraph data={data.sites} />
        </div>
      </div>

      {/* Director */}
      <div className="text-left text-gray-700 leading-relaxed">
        <div className="text-blue-500 font-semibold uppercase text-sm pb-2">
          Program director
        </div>
        <div className="bg-blue-100 border border-blue-200 p-2">
          <div className="flex justify-between items-start">
            <div className="flex-1 self-center mr-1 text-gray-800 font-medium">
              {data.director.name}
            </div>
            <div className="bg-blue-200 text-blue-700 px-2 py-1px rounded-lg font-normal text-sm">
              Appointed{' '}
              {dayjs(data.director.directorFirstAppointed).from(dayjs())}
            </div>
          </div>
          {data.director.title && <div>{data.director.title}</div>}
          {data.about.email && <Email address={data.about.email} />}
          {data.about.phone && <div>{data.about.phone}</div>}
        </div>
      </div>

      {/* Coordinators */}
      {data.coordinators?.length && (
        <div className="text-left text-gray-700 leading-relaxed">
          <div className="text-blue-500 font-semibold uppercase text-sm pb-2">
            Program coordinator
          </div>
          {data.coordinators.slice(0, 1).map(o => (
            <div
              key={o.name}
              className="mb-4 bg-blue-100 border border-blue-200 p-2"
            >
              <div className="font-medium text-gray-800">{o.name}</div>
              <div className="">
                {o.title && <div>{o.title}</div>}
                {o.email && <Email address={o.email} />}
                {o.phone && <div>{o.phone}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* More info */}
      {
        <div className="text-center my-2 py-6 px-4 -mx-4 -mt-4 text-gray-800 bg-gray-200">
          {url && (
            <>
              More information at
              <a
                href={url.href}
                target="_blank"
                className="ml-1 underline inline"
                rel="noopener"
              >
                {url.hostname}
              </a>
              .
            </>
          )}
          {/* Last updated */}
          {data.updatedAt && (
            <div className="text-gray-600">
              <span className="pr-1">Last updated</span>
              <span>{dayjs(data.updatedAt).format('MMMM YYYY')}.</span>
            </div>
          )}
        </div>
      }
    </div>
  );
};

export default memo(ProgramDetails);
